import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { DialogBody, DialogClose, DialogFooter } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import { PublicAnnouncement } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { useCreateAnnouncement } from "../api/add-announcement";
import { announcementsQueryKey } from "../api/get-announcements";
import {
  AddAnnouncementFormData,
  addAnnouncementSchema,
} from "../types/add-announcement-schema";

import { AnnouncementElement } from "./announce.element";

type AddAnnouncementFormProps = {
  onClose: () => void;
};

export function AddAnnouncementForm(props: AddAnnouncementFormProps) {
  const { onClose } = props;
  const form = useForm<AddAnnouncementFormData>({
    resolver: zodResolver(addAnnouncementSchema),
    defaultValues: {
      type: undefined,
      title: "",
      message: "",
    },
  });

  const { mutateAsync: createAnnouncement, isSuccess } = useCreateAnnouncement({
    onSuccess: async (result) => {
      queryClient.setQueryData(
        announcementsQueryKey,
        (oldData: PublicAnnouncement[] | undefined) => {
          if (!oldData) return oldData;
          return [result, ...oldData];
        },
      );
      await queryClient.invalidateQueries({
        queryKey: announcementsQueryKey,
      });
    },
  });

  async function handleCreateAnnouncement(data: AddAnnouncementFormData) {
    try {
      await createAnnouncement({
        type: data.type,
        title: data.title,
        message: data.message,
        startDate: data.date.from.toISOString(),
        endDate: data.date.to.toISOString(),
        // If no organisation IDs are provided, the announcement is global
        isGlobal: !data.organisations || data.organisations.length === 0,
        organisationIds:
          !data.organisations || data.organisations.length === 0
            ? undefined
            : data.organisations.map((organisation) => organisation.value),
      });
      onClose();
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleCreateAnnouncement)}>
        <DialogBody>
          <AnnouncementElement />
        </DialogBody>
        <DialogFooter className="sm:justify-end">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="submit"
            isLoading={form.formState.isSubmitting || isSuccess}
            isAnimated
          >
            Schedule Announcement
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
